import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaChartArea } from "react-icons/fa";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  ChartWrapper,
  ChartTitle,
  ChartContainer,
  LoadingContainer,
  CustomTooltip,
} from "./SalesChart.styles";
import { getSalesChartData } from "../../store/slices/admin/asyncThunks";
import {
  selectSalesChartData,
  selectSalesChartDataLoading,
} from "../../store/slices/admin/slice";

const SalesChart = ({ timeframe }) => {
  const dispatch = useDispatch();
  const chartData = useSelector(selectSalesChartData);
  const loading = useSelector(selectSalesChartDataLoading);

  useEffect(() => {
    dispatch(getSalesChartData(timeframe));
  }, [dispatch, timeframe]);

  // Fallback data generation (keeping for reference)
  const generateFallbackData = () => {
    const data = [];
    const today = new Date();

    if (timeframe === "7days") {
      // Last 7 days
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const value = Math.floor(Math.random() * 8000) + 2000;

        data.push({
          name: date.toLocaleDateString("sr-latn-RS", {
            day: "numeric",
            month: "short",
          }),
          sales: value,
          fullDate: date.toLocaleDateString("sr-latn-RS"),
        });
      }
    } else if (timeframe === "30days") {
      // Last 30 days, show every 3rd day
      for (let i = 30; i >= 0; i -= 3) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const value = Math.floor(Math.random() * 12000) + 5000;

        data.push({
          name: date.toLocaleDateString("sr-latn-RS", {
            day: "numeric",
            month: "short",
          }),
          sales: value,
          fullDate: date.toLocaleDateString("sr-latn-RS"),
        });
      }
    } else if (timeframe === "3months") {
      // Last 3 months, weekly data
      for (let i = 12; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i * 7);
        const value = Math.floor(Math.random() * 20000) + 8000;

        data.push({
          name: date.toLocaleDateString("sr-latn-RS", {
            day: "numeric",
            month: "short",
          }),
          sales: value,
          fullDate: date.toLocaleDateString("sr-latn-RS"),
        });
      }
    } else if (timeframe === "1year") {
      // Last 12 months
      for (let i = 11; i >= 0; i--) {
        const date = new Date(today);
        date.setMonth(date.getMonth() - i);
        const value = Math.floor(Math.random() * 40000) + 15000;

        data.push({
          name: date.toLocaleDateString("sr-latn-RS", {
            month: "short",
            year: "2-digit",
          }),
          sales: value,
          fullDate: date.toLocaleDateString("sr-latn-RS", {
            month: "long",
            year: "numeric",
          }),
        });
      }
    }

    return data;
  };

  // Use real data if available, otherwise use fallback
  const displayData =
    chartData && chartData.length > 0 ? chartData : generateFallbackData();

  // Custom tooltip component
  const CustomTooltipContent = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <CustomTooltip>
          <div className="tooltip-label">{data.fullDate}</div>
          <div className="tooltip-value">
            {new Intl.NumberFormat("sr-latn-RS").format(payload[0].value)} RSD
          </div>
        </CustomTooltip>
      );
    }
    return null;
  };

  const formatTimeframeName = (timeframe) => {
    const names = {
      "7days": "Poslednja 7 dana",
      "30days": "Poslednji mesec",
      "3months": "Poslednja 3 meseca",
      "1year": "Poslednja godina",
      all: "Od početka rada",
    };
    return names[timeframe] || "Prodaja";
  };

  if (loading) {
    return (
      <ChartWrapper>
        <ChartTitle>
          <FaChartArea />
          Pregled Prodaje - {formatTimeframeName(timeframe)}
        </ChartTitle>
        <LoadingContainer>
          <l-line-spinner
            size="32"
            stroke="3"
            speed="1"
            color="var(--accent)"
          />
          <span>Učitavanje podataka...</span>
        </LoadingContainer>
      </ChartWrapper>
    );
  }

  return (
    <ChartWrapper>
      <ChartTitle>
        <FaChartArea />
        Pregled Prodaje - {formatTimeframeName(timeframe)}
      </ChartTitle>

      <ChartContainer>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={displayData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <defs>
              <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.3} />
                <stop
                  offset="95%"
                  stopColor="var(--accent)"
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--accent-light)"
              opacity={0.3}
            />

            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: "var(--text-secondary)",
                fontWeight: 500,
              }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 12,
                fill: "var(--text-secondary)",
                fontWeight: 500,
              }}
              tickFormatter={(value) =>
                new Intl.NumberFormat("sr-latn-RS", {
                  notation: "compact",
                  compactDisplay: "short",
                }).format(value)
              }
            />

            <RechartsTooltip content={<CustomTooltipContent />} />

            <Area
              type="monotone"
              dataKey={chartData ? "total" : "sales"}
              stroke="var(--accent)"
              strokeWidth={3}
              fill="url(#salesGradient)"
              dot={{
                fill: "var(--accent)",
                strokeWidth: 2,
                stroke: "var(--surface)",
                r: 4,
              }}
              activeDot={{
                r: 6,
                fill: "var(--accent)",
                stroke: "var(--surface)",
                strokeWidth: 3,
              }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartContainer>
    </ChartWrapper>
  );
};

export default SalesChart;
